import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { api } from '../utils/api';
import BoothPicker from '../components/BoothPicker';
import { Clock, AlertTriangle, CheckCircle, Coffee, Lock, ChevronDown, ChevronUp, CornerDownRight, PencilLine } from 'lucide-react';
import { acqDenominator, invalidSeatCount, sumValidSeat } from '../utils/seat-rate.mjs';
import { inputSlot, DEFAULT_GRACE_MINUTES } from '../utils/input-slot.mjs';

const SLOTS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];
const todayJst = () => new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
const jstHourNow = () => new Date(Date.now() + 9 * 3600000).getUTCHours();
// JST の0時からの経過分。30秒ごとに読み直すが、分が変わったときだけ再描画される（数値なので）。
const jstMinuteOfDay = () => { const d = new Date(Date.now() + 9 * 3600000); return d.getUTCHours() * 60 + d.getUTCMinutes(); };
const pct = (a, b) => (b ? ((a / b) * 100).toFixed(1) + '%' : '-');

// 色は持たせない。数字そのものが主役で、色は意味（獲得・失注など）にだけ使う。
const METRICS = [
  { key: 'catch',     label: 'キャッチ' },
  { key: 'sticker',   label: 'ティッシュ' },
  { key: 'talk',      label: 'トーク' },
  { key: 'full_talk', label: 'フルトーク' },
  { key: 'seat',      label: '着座', auto: true },
  { key: 'acq',       label: '獲得', auto: true },
];

const RATIOS = [
  { label: 'ティッシュ率',   num: 'sticker',   den: 'catch' },
  { label: 'トーク率',       num: 'talk',      den: 'sticker' },
  { label: 'フルトーク率',   num: 'full_talk', den: 'talk' },
  { label: '着座率',         num: 'seat',      den: 'full_talk' },
  { label: '獲得率',         num: 'acq',       den: 'valid_seat' }, // 分母は有効着座（ADR 0028）
  { label: 'キャッチ→獲得',  num: 'acq',       den: 'catch' },
];

const panel = { background: 'var(--field)', border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', padding: 'var(--s3)', marginBottom: 'var(--s3)' };
const note  = { fontSize: 'var(--t-note)', color: 'var(--ink-mute)', lineHeight: 1.7 };

// 手入力するカウンター（キャッチ／ティッシュ／トーク／フルトーク）用の記入枠。
// ラベルは PC では見出し帯にあるので隠し、スマホでは1行まるごと与える（CSS側で切替）。
function Cell({ label, value, onChange }) {
  return (
    <label className="fcell">
      <span className="fcell__label">{label}</span>
      <input
        className="fcell__input num"
        type="number" inputMode="numeric" pattern="[0-9]*" min="0"
        value={value === 0 || value === '' || value == null ? '' : value}
        placeholder="0"
        aria-label={label}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === '' ? 0 : Math.max(0, parseInt(v, 10) || 0));
        }}
      />
    </label>
  );
}

// 着座・獲得は着座メモからの自動集計のみ（手入力不可・ADR 0013）。
// pinned = 手入力を止める前に保存された seat_override / acq_override が残っている枠。
// 過去の集計を変えないため値は表示し続けるが、メモ由来でないことが分かるようにする。
function AutoCell({ label, value, pinned }) {
  return (
    <div className="fcell fcell--auto">
      <span className="fcell__label">{label}</span>
      <span className={`fcell__value num${value ? '' : ' is-zero'}`}>{value || 0}</span>
      <span className={`fcell__note${pinned ? ' is-pinned' : ''}`}>{pinned ? '手入力' : 'メモ'}</span>
    </div>
  );
}

export default function HourlyInput({ user }) {
  const [today, setToday] = useState(todayJst());
  const [date, setDate] = useState(today);
  const [mine, setMine] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingHour, setSavingHour] = useState(null);
  const [savedHour, setSavedHour] = useState(null);
  const [failedHours, setFailedHours] = useState({}); // { 時: 直前に送ろうとした行 } 保存に失敗した枠
  const [booths, setBooths] = useState([]);
  const [boothName, setBoothName] = useState('');
  const [savingBooth, setSavingBooth] = useState(false);
  const [dateRolled, setDateRolled] = useState(false); // 日付が変わって画面が切り替わった告知
  const [nowMinute, setNowMinute] = useState(jstMinuteOfDay()); // 「入力する枠」を決めるため（ADR 0031）
  const [showRatios, setShowRatios] = useState(false);  // 率を開いているか
  const [openHours, setOpenHours] = useState({});       // スマホで手を広げた枠（表示だけの状態）
  const [memoOpen, setMemoOpen] = useState({});         // メモ欄を開いた枠（表示だけの状態）
  const timers = useRef({});
  const rowRefs = useRef({});        // 時間枠の DOM（「いま」の枠へ寄せるのに使う）
  const scrolledFor = useRef(null);  // 自動スクロール済みの日付（1日1回だけ寄せる）

  const load = useCallback(() => {
    setLoading(true);
    setFailedHours({}); // 別の日を開いたら前の日の失敗表示は持ち越さない
    api.getHourly(date)
      .then((res) => { setMine(res.mine || {}); setBoothName(res.booth_name || ''); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [date]);
  useEffect(() => { load(); }, [load]);

  // ブース一覧を取得（マウント時）
  useEffect(() => { api.getBooths().then(setBooths).catch(() => setBooths([])); }, []);

  // 日付またぎ対策：PWAを開いたまま/バックグラウンド復帰で日付が変わったら、
  // 「今日」を見ていたユーザーを新しい今日へ自動で追従させる（古い日付のまま残るのを防ぐ）。
  const todayRef = useRef(today);
  useEffect(() => {
    const check = () => {
      setNowMinute(jstMinuteOfDay());
      const t = todayJst();
      if (t === todayRef.current) return;
      const prev = todayRef.current;
      todayRef.current = t;
      setToday(t);
      setDate((d) => {
        if (d !== prev) return d;          // 過去日を意図的に見ている→そのまま
        setDateRolled(true);               // 今日を表示中→新しい今日へ切替＋告知（前日ぶんは保存済み）
        return t;
      });
    };
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    const id = setInterval(check, 30000);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', check);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', check); };
  }, []);

  // その日のブース（1日1ブース）を保存。新規名は createBooth で作成してから id を解決。
  const changeBooth = async (name) => {
    setBoothName(name);
    setSavingBooth(true);
    try {
      let boothId = null;
      const nm = (name || '').trim();
      if (nm) {
        const found = booths.find((b) => b.name === nm);
        if (found) { boothId = found.id; }
        else {
          // createBooth の応答は { id, message } で name を含まない。name 付きで一覧に足さないと
          // 検索時の b.name.toLowerCase() で undefined 参照になりクラッシュ／再選択で重複作成されるため、
          // 入力した nm を name として補完する（id 重複はガード）。
          const created = await api.createBooth({ name: nm });
          boothId = created.id;
          setBooths((bs) => (bs.some((b) => b.id === created.id) ? bs : [...bs, { id: created.id, name: nm }]));
        }
      }
      await api.saveHourlyBooth(date, boothId);
    } catch (e) { alert(e.message); }
    setSavingBooth(false);
  };

  // 保存に失敗しても alert は出さない（数字を打っている最中にモーダルが割り込むと入力が止まり、
  // しかも一度消すと失敗したことが分からなくなる）。その枠に赤い「再送」を出し続ける。
  const saveRow = (hour, row) => {
    setSavingHour(hour);
    api.saveHourlyCell({
      kpi_date: date, hour,
      catch: row.catch || 0, sticker: row.sticker || 0, talk: row.talk || 0, full_talk: row.full_talk || 0,
      // 空文字で送ると Worker 側で null になる（＝メモを消せる）
      memo: row.memo || '',
      // 着座・獲得は送らない（着座メモが唯一の入力元。ADR 0013）。
      // 未送信なら Worker 側は既存値を保持するので、過去に入った上書きは消さない。
      off_duty: row.off_duty ? 1 : 0,
    })
      .then(() => {
        setFailedHours((f) => { if (!(hour in f)) return f; const n = { ...f }; delete n[hour]; return n; });
        setSavedHour(hour); setTimeout(() => setSavedHour((h) => (h === hour ? null : h)), 1500);
      })
      .catch(() => setFailedHours((f) => ({ ...f, [hour]: row })))
      .finally(() => setSavingHour((h) => (h === hour ? null : h)));
  };

  const scheduleSave = (hour, row) => {
    clearTimeout(timers.current[hour]);
    timers.current[hour] = setTimeout(() => saveRow(hour, row), 600);
  };

  const setField = (hour, key, val) => {
    setMine((prev) => {
      const cur = prev[hour] || {};
      // 着座・獲得はここに来ない（AutoCell は onChange を持たない）。
      const next = { ...cur, [key]: val };
      const updated = { ...prev, [hour]: next };
      scheduleSave(hour, next);
      return updated;
    });
  };

  // 休憩/非稼働の切替。即保存。ONにした枠は未入力アラートの対象外。
  const toggleOff = (hour) => {
    setMine((prev) => {
      const cur = prev[hour] || {};
      const next = { ...cur, off_duty: cur.off_duty ? 0 : 1 };
      const updated = { ...prev, [hour]: next };
      clearTimeout(timers.current[hour]);
      saveRow(hour, next);
      return updated;
    });
  };

  const isOff = (h) => !!(mine[h] && mine[h].off_duty);
  const hasData = (h) => {
    const m = mine[h] || {};
    return !!(m.catch || m.sticker || m.talk || m.full_talk)
      || m.seat_override != null || m.acq_override != null || !!m.seat_auto || !!m.acq_auto;
  };
  // 稼働ウィンドウ＝入力または休憩マークがある最初〜最後のスロット。
  // これより前（遅い出勤）・後（早い退勤）は未入力の判定対象にしない。
  const activeHours = SLOTS.filter((h) => hasData(h) || isOff(h));
  const firstActive = activeHours.length ? Math.min(...activeHours) : null;
  const lastActive = activeHours.length ? Math.max(...activeHours) : null;
  const isPastOrCurrent = (h) => date === today && h <= jstHourNow();
  // 未入力＝当日・過去/現在・稼働ウィンドウ内・データ無し・休憩でない（＝中抜けの入れ忘れ）
  const isMissing = (h) =>
    isPastOrCurrent(h) && firstActive != null && h >= firstActive && h <= lastActive
    && !hasData(h) && !isOff(h);
  const unfilled = SLOTS.filter(isMissing);

  // 個人合計（自分の時間別入力の合計）。mine から算出するので入力に即時追従する。
  const day = useMemo(() => {
    const t = { catch: 0, sticker: 0, talk: 0, full_talk: 0, seat: 0, acq: 0 };
    SLOTS.forEach((h) => {
      const m = mine[h] || {};
      t.catch += m.catch || 0; t.sticker += m.sticker || 0; t.talk += m.talk || 0;
      t.full_talk += m.full_talk || 0; t.seat += m.seat || 0; t.acq += m.acq || 0;
    });
    // 獲得率の分母。時間帯ごとの有効着座（サーバが返す）を合計する（ADR 0028）
    t.valid_seat = sumValidSeat(SLOTS.map((h) => mine[h]).filter(Boolean));
    return t;
  }, [mine]);
  const isToday = date === today;
  // ブース未選択なら日報（時間別入力）を書けない＝入力欄をロックする（ADR 0004: ブースはその日1つ）。
  const boothSelected = !!(boothName || '').trim();
  // 「入力する枠」（ADR 0031）。時間別入力はその1時間が終わってから入れることが多いので、
  // 毎正時から29分までは直前に終わった枠（13:05 なら 12:00〜13:00）、30分以降は今の枠。当日だけ。
  // 変数名は従来どおり currentHour（畳む・寄せる・メモ欄を開く判定がすべてこれを見る）。
  const target = isToday
    ? inputSlot({ hour: Math.floor(nowMinute / 60), minute: nowMinute % 60, slots: SLOTS })
    : null;
  const currentHour = target ? target.hour : null;
  const isPreviousTarget = target?.kind === 'previous';

  // 30分を過ぎて入力する枠が今の枠へ移っても、それまで開いていた枠は開いたままにする。
  // 入力の途中で畳まれて、書いていた欄が消えたように見えるのを防ぐ。
  const prevTargetRef = useRef(currentHour);
  useEffect(() => {
    const prev = prevTargetRef.current;
    prevTargetRef.current = currentHour;
    if (prev != null && currentHour != null && prev !== currentHour) {
      setOpenHours((o) => ({ ...o, [prev]: true }));
    }
  }, [currentHour]);

  // スマホでは「入力する枠」だけを開き、ほかの枠は1行に畳む（13枠ぶん約3,600px のスクロールを避ける）。
  // 入力欄は DOM に残したまま CSS で隠すだけなので、開き直しても値と保存中の状態は消えない。
  // 当日以外・保存失敗・手で開いた枠は常に開く。PC では .hrow--collapsed に効くルールが無いので影響しない。
  const isCollapsed = (h) =>
    currentHour != null && h !== currentHour && !openHours[h] && !failedHours[h];
  const toggleOpen = (h) => setOpenHours((o) => ({ ...o, [h]: !o[h] }));

  const scrollToHour = (h) => {
    const el = rowRefs.current[h];
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  // 13枠ぶんあるので、開いた直後は「入力する枠」まで寄せる。
  // 画面に入っているなら動かさない（勝手に飛ぶのを防ぐ）。日付ごとに1回だけ。
  useEffect(() => {
    if (loading || !boothSelected || currentHour == null) return;
    if (scrolledFor.current === date) return;
    const el = rowRefs.current[currentHour];
    if (!el) return;
    scrolledFor.current = date;
    const r = el.getBoundingClientRect();
    const top = 140;                       // 固定ヘッダーの下
    const bottom = window.innerHeight - 90; // ボトムナビの上
    if (r.top < top || r.bottom > bottom) el.scrollIntoView({ block: 'center' });
  }, [loading, boothSelected, currentHour, date]);

  const dateBtn = (activeFlag) => ({
    minHeight: 44, padding: '0 var(--s3)', borderRadius: 'var(--r-sm)',
    border: `1px solid ${activeFlag ? 'var(--mark)' : 'var(--rule-strong)'}`,
    background: activeFlag ? 'var(--mark-soft)' : 'var(--field)',
    color: activeFlag ? 'var(--mark)' : 'var(--ink-soft)',
    fontWeight: 600, fontSize: 'var(--t-body)', cursor: 'pointer',
  });

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      <div style={{ marginBottom: 'var(--s4)' }}>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>1時間ごとに入力</div>
        <h1 className="page-title" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 'var(--s2)' }}>
          <Clock size={20} /> 時間別入力
        </h1>
      </div>

      {/* 日付 */}
      <div style={{ ...panel, display: 'flex', gap: 'var(--s2)', alignItems: 'center' }}>
        <input className="form-input num" type="date" value={date} max={today} onChange={(e) => { setDateRolled(false); setDate(e.target.value); }} style={{ flex: 1 }} />
        <button onClick={() => { setDateRolled(false); setDate(new Date(Date.now() - 86400000 + 9 * 3600000).toISOString().slice(0, 10)); }} style={dateBtn(false)}>昨日</button>
        <button onClick={() => { setDateRolled(false); setDate(today); }} style={dateBtn(isToday)}>今日</button>
      </div>

      {dateRolled && (
        <div className="alert-error" style={{ background: 'var(--hold-bg)', color: 'var(--hold)', borderColor: '#5C4718', display: 'flex', alignItems: 'center', gap: 'var(--s2)' }}>
          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, lineHeight: 1.6 }}>
            日付が変わったので今日（{today}）の入力に切り替えました。前の日の入力は保存済みです。
          </span>
          <button onClick={() => setDateRolled(false)} style={{ background: 'transparent', border: 'none', color: 'var(--hold)', fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: '0 var(--s1)' }}>OK</button>
        </div>
      )}

      {/* ブース（その日の店舗・1日1ブース）※必須。未選択だと下の時間別入力はロック。 */}
      <div style={{ ...panel, borderColor: boothSelected ? 'var(--rule)' : 'var(--loss)' }}>
        <BoothPicker booths={booths} value={boothName} onChange={changeBooth} required />
        <div style={{ ...note, marginTop: 'var(--s2)' }}>
          {savingBooth ? '保存中…' : 'この日の着座メモにも自動で同じブースが反映されます。'}
        </div>
      </div>

      {/* ブース未選択のあいだは時間別入力をロック（＝ブースを選ばないと日報を書けない） */}
      {!boothSelected ? (
        <div style={{ ...panel, padding: 'var(--s8) var(--s4)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--s3)' }}>
          <Lock size={24} color="var(--ink-mute)" />
          <div style={{ fontSize: 'var(--t-lead)', fontWeight: 600 }}>まず本日のブースを選択してください</div>
          <div style={{ ...note, maxWidth: 380 }}>
            ブースを選ぶと、この下に時間別のKPI入力欄が表示されます。<br />ブース未選択のまま日報は入力できません。
          </div>
        </div>
      ) : (
      <>
      {/* 保存できていない枠（スクロールで見失わないよう先頭にも出す） */}
      {Object.keys(failedHours).length > 0 && (
        <div className="alert-error" style={{ display: 'flex', alignItems: 'center', gap: 'var(--s2)' }}>
          <AlertTriangle size={18} style={{ flexShrink: 0 }} />
          <div style={{ lineHeight: 1.6 }}>
            保存できていない時間が {Object.keys(failedHours).length} 枠あります（{Object.keys(failedHours).map((h) => `${h}時`).join('・')}）。
            電波の良い場所で、その枠の「再送」を押してください。
          </div>
        </div>
      )}

      {/* 未入力リマインダー（当日のみ） */}
      {unfilled.length > 0 && (
        <div className="alert-error" style={{ background: 'var(--hold-bg)', color: 'var(--hold)', borderColor: '#5C4718', display: 'flex', alignItems: 'center', gap: 'var(--s2)' }}>
          <AlertTriangle size={18} style={{ flexShrink: 0 }} />
          <div>未入力の時間が {unfilled.length} 枠あります（{unfilled.map((h) => `${h}時`).join('・')}）</div>
        </div>
      )}

      {/* 入力する枠へ戻る。一度スクロールで離れると戻れなかったのを直す。 */}
      {currentHour != null && (
        <button
          onClick={() => scrollToHour(currentHour)}
          className="btn-secondary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s1)', marginBottom: 'var(--s3)' }}
        >
          <CornerDownRight size={15} /> {isPreviousTarget ? '入力する時間' : 'いまの時間'}（{currentHour}:00〜）へ
        </button>
      )}

      {loading && <div style={{ ...note, textAlign: 'center', padding: 'var(--s3)' }}>読み込み中...</div>}

      {/* ===== 台帳 ===== */}
      <div className="ledger">
        {/* 見出しは13回繰り返さず、ここに1回だけ置く（PCのみ表示） */}
        <div className="ledger__head">
          <span>時間</span>
          {METRICS.map((m) => <span key={m.key}>{m.label}</span>)}
          <span>　</span>
        </div>

        {SLOTS.map((h) => {
          const m = mine[h] || {};
          const off = isOff(h);
          const flagged = isMissing(h);
          const isNow = h === currentHour;
          const collapsed = isCollapsed(h);
          // 入力する枠は最初からメモ欄も開く（書くならこの枠なので）
          const showMemo = !!(m.memo || '').trim() || isNow || !!memoOpen[h];
          const cls = [
            'hrow',
            isNow ? 'is-now' : '',
            off ? 'is-off' : '',
            flagged ? 'is-missing' : '',
            failedHours[h] ? 'is-failed' : '',
            collapsed ? 'hrow--collapsed' : '',
          ].filter(Boolean).join(' ');

          return (
            <div key={h} ref={(el) => { rowRefs.current[h] = el; }} className={cls}>
              <div
                className="hrow__time"
                onClick={() => toggleOpen(h)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleOpen(h); } }}
              >
                {h}:00〜{h + 1}:00
                {isNow && <span className="hrow__tag">{isPreviousTarget ? '入力' : 'いま'}</span>}
                {(m.memo || '').trim() && <PencilLine size={14} color="var(--mark)" aria-label="メモあり" />}
                {savingHour === h && <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>保存中…</span>}
                {savedHour === h && <span style={{ fontSize: 'var(--t-note)', color: 'var(--gain)', display: 'inline-flex', alignItems: 'center', gap: 2 }}><CheckCircle size={13} /> 保存</span>}
              </div>

              {/* 畳んでいるときの1行サマリー（スマホのみ表示） */}
              <div className="hrow__summary num">
                {off ? '休憩・非稼働'
                  : hasData(h)
                    ? METRICS.map((mt) => `${mt.label} ${mt.key === 'seat' ? (m.seat || 0) : mt.key === 'acq' ? (m.acq || 0) : (m[mt.key] || 0)}`).join('　')
                    : '未入力'}
                {(m.memo || '').trim() && (
                  <span style={{ display: 'block', color: 'var(--mark)', marginTop: 2 }}>{m.memo}</span>
                )}
              </div>

              <div className="hrow__fields" style={{ opacity: off ? 0.45 : 1 }}>
                {METRICS.map((mt) => {
                  if (mt.auto) {
                    const pinned = mt.key === 'seat' ? m.seat_override != null : m.acq_override != null;
                    const value = mt.key === 'seat' ? m.seat : m.acq;
                    return <AutoCell key={mt.key} label={mt.label} value={value || 0} pinned={pinned} />;
                  }
                  return (
                    <Cell
                      key={mt.key}
                      label={mt.label}
                      value={m[mt.key] || 0}
                      onChange={(v) => setField(h, mt.key, v)}
                    />
                  );
                })}
              </div>

              <div className="hrow__actions">
                {failedHours[h] && savingHour !== h ? (
                  <button type="button" onClick={() => saveRow(h, failedHours[h])} className="btn-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s1)' }}>
                    <AlertTriangle size={13} /> <span className="btn-label">再送</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => toggleOff(h)}
                    className="btn-secondary"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 'var(--s1)', padding: '0 var(--s2)',
                      borderColor: off ? 'var(--ink-mute)' : 'var(--rule-strong)',
                      color: off ? 'var(--ink)' : 'var(--ink-mute)',
                      background: off ? 'var(--paper-sunk)' : 'var(--field)',
                    }}
                  >
                    <Coffee size={13} /> <span className="btn-label">{off ? '休憩中' : '休憩'}</span>
                  </button>
                )}
                {!showMemo && (
                  <button
                    type="button"
                    onClick={() => setMemoOpen((o) => ({ ...o, [h]: true }))}
                    className="btn-secondary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s1)', padding: '0 var(--s2)', color: 'var(--ink-mute)' }}
                    aria-label={`${h}時の気づきを書く`}
                  >
                    <PencilLine size={13} /> <span className="btn-label">メモ</span>
                  </button>
                )}
              </div>

              {/* 気づきは常時出すと1枠が3段になり台帳が間延びする。
                  いまの時間・既に書いてある枠・手で開いた枠だけに出す。 */}
              {showMemo && (
                <label className="hrow__memo">
                  <PencilLine size={15} color="var(--ink-mute)" style={{ flexShrink: 0 }} />
                  <input
                    type="text"
                    value={m.memo || ''}
                    onChange={(e) => setField(h, 'memo', e.target.value)}
                    placeholder="この時間の気づき（例：キャッチの声が小さい→次は止まり際に張る）"
                    aria-label={`${h}時の気づき`}
                    autoFocus={!!memoOpen[h] && !(m.memo || '').trim()}
                  />
                </label>
              )}
            </div>
          );
        })}

        {/* 合計は台帳の最後に置く（紙のタリーシートと同じ位置） */}
        <div className="ledger__foot">
          <span className="lbl">合計（{isToday ? '本日' : date}）</span>
          {METRICS.map((mt) => <span key={mt.key} className="v num">{day[mt.key] || 0}</span>)}
          <span />
        </div>
      </div>

      {/* 率は既定で閉じる。入力中に見るものではないため。 */}
      <button
        type="button"
        onClick={() => setShowRatios((v) => !v)}
        className="btn-secondary"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s1)', margin: 'var(--s3) 0' }}
      >
        {showRatios ? <ChevronUp size={15} /> : <ChevronDown size={15} />} 転換率を{showRatios ? '閉じる' : '見る'}
      </button>

      {showRatios && (
        <div style={{ ...panel, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--s3)' }}>
          {RATIOS.map((r) => (
            <div key={r.label} style={{ borderLeft: '2px solid var(--rule)', paddingLeft: 'var(--s3)' }}>
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)' }}>{r.label}</div>
              <div className="num" style={{ fontSize: 'var(--t-head)', fontWeight: 700 }}>{pct(day[r.num] || 0, day[r.den] || 0)}</div>
              {r.den === 'valid_seat' && invalidSeatCount(day) > 0 && (
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }} title={`「有効じゃない」${invalidSeatCount(day)}件を分母から除いています`}>有効着座 {acqDenominator(day)}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ ...note, padding: 'var(--s2) 0' }}>
        ※ <strong style={{ color: 'var(--ink)' }}>着座・獲得はここでは入力できません。</strong>着座メモの記録時刻から自動集計されます。
        クローザーとして渡された着座も自分の着座に入り、<strong style={{ color: 'var(--ink)' }}>着座メモが記録された時刻の枠</strong>に立ちます（対応した時刻ではありません）。
        数が合わないときは<strong style={{ color: 'var(--ink)' }}>着座メモ</strong>を追加・修正してください。<br />
        ※ 着座・獲得の下の「メモ」は着座メモから自動集計した値、<span style={{ color: 'var(--hold)' }}>「手入力」</span>は手入力を止める前に保存された古い値です（過去の集計を変えないため残しています）。<br />
        ※ 休憩・中抜けは各時間の「休憩」を押すと未入力アラートの対象外になります。出勤前・退勤後（最初〜最後の入力の外）の時間は自動で対象外です。<br />
        ※ スマホでは入力する時間だけを開いています（毎正時から{DEFAULT_GRACE_MINUTES - 1}分までは直前の時間、それ以降は今の時間）。ほかの時間を直すときは、その時間の見出しを押すと開きます。
      </div>
      </>
      )}
    </div>
  );
}
